<template>
  <div class="result-view">
    <section class="section section--booking-engine">
      <trigger-booking-engine :is_open="is_open" @toggle="is_open = $event" />
      <booking-engine size="sm" :is_open="is_open" @search="onSearch" />
    </section>

    <b-container>
      <section v-if="loading && !show_filters" class="section section--loader">
        <loader
          :key="searching_suggestions ? 'suggestions' : 'search'"
          size="large"
          :show_timing_slot="true"
          :text_loading="loader_messages"
        />
      </section>

      <section v-if="!loading || show_filters" class="section section--filters">
        <b-row>
          <b-col
            cols="3"
            class="filters__sticky-wrapper"
            :class="{ visible: show_filters }"
          >
            <div class="result-view--sticky">
              <filters
                :filtered_all_carriers="filtered_flights.filtered_all_carriers"
                :flights_count="
                  ((filtered_flights || []).filtered || []).length
                "
                :is-open="show_filters"
                @toggle-filters="toggle_filters"
              />
            </div>
          </b-col>

          <b-col xs="12" class="result-view--main">
            <search-error v-if="show_search_error" @retry="onSearch" />
            <!-- After a search by date suggestion; no-results-suggestions renders its own -->
            <date-suggestions v-if="!has_suggestions" />
            <div v-if="has_results" class="flightoffer">
              <div class="flightoffer--wrap">
                <div>
                  <div class="filters__btn-container">
                    <vb-button
                      class="filters__btn-toggle variant-white"
                      :vb_options="{
                        size: 'sm',
                        icon: 'filters',
                        text: 'Filtri',
                      }"
                      @click="toggle_filters"
                    />
                    <vb-dropdown
                      class="filters__btn-order variant-white"
                      :vb_options="{
                        text: sorting.find((s) => s.by === current_sort)
                          .description,
                        size: 'sm',
                        right: true,
                      }"
                    >
                      <b-dropdown-item
                        v-for="(s, index) in sorting"
                        :key="_uid + 'sort' + index"
                        :active="current_sort === s.by"
                        @click="
                          () => {
                            set_sorting(s.by);
                            scroll_top();
                          }
                        "
                      >
                        Ordina per {{ s.title.toLowerCase() }}
                      </b-dropdown-item>
                    </vb-dropdown>
                  </div>
                  <dynamic-scroller
                    ref="resultsListScroller"
                    page-mode
                    :items="items"
                    :item-size="null"
                    :min-item-size="100"
                    key-field="_key"
                    class="scroller"
                  >
                    <template v-slot="{ item, index, active }">
                      <dynamic-scroller-item
                        :item="item"
                        class="pb-4"
                        :data-index="index"
                        :class="`flight--pad-${index}`"
                        :active="active"
                      >
                        <component
                          :is="item.component"
                          v-if="item.component"
                          v-bind="item.props"
                        />
                        <flight-pad
                          v-else
                          :flight_offer="item"
                          :flight_offer_index="index"
                          :is_best="
                            Object.keys(best_picks).reduce(
                              (list, pick) =>
                                best_picks[pick].flight_offer_id === item.id
                                  ? [...list, pick]
                                  : list,
                              []
                            )
                          "
                          selectable
                        />
                      </dynamic-scroller-item>
                    </template>
                  </dynamic-scroller>
                </div>
              </div>
            </div>
            <no-results-suggestions v-else-if="has_suggestions" />
            <no-results v-else @open-filters="open_filters" />
          </b-col>
        </b-row>
      </section>
    </b-container>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex';
  import BookingEngine from '@liveflight/components/blocks/booking-engine/booking-engine';
  import FlightPad from '@liveflight/components/blocks/flight-pad/flight-pad';
  import Filters from '@liveflight/components/blocks/filters/filters';
  import NoResults from '@liveflight/components/blocks/no-results/no-results';
  import NoResultsSuggestions from '@liveflight/components/blocks/no-results/no-results-suggestions';
  import DateSuggestions from '@liveflight/components/blocks/no-results/date-suggestions';
  import SearchError from '@liveflight/components/blocks/search-error/search-error';
  import { filter_flight_offers } from '@liveflight/utilities/filters';
  import { parse_flight_offer_for_ecommerce_item } from '@liveflight/utilities/mixins';
  import { custom_event } from '@/utilities/commons';
  import {
    sort_flight_offers,
    set_sorting,
    get_sorting,
    sorting_options,
  } from '@liveflight/utilities/sorting';
  import { add_extra_components_to_results, get_config } from '@/utilities';
  import MarketingPad from '@/components/blocks/marketing-pad.vue';
  import { search_all, search_all_suggestions } from '@liveflight/providers';
  import TriggerBookingEngine from '@components/atoms/trigger-booking-engine.vue';
  import { loading_messages } from '@/resources';

  export default {
    name: 'liveflight-search',
    components: {
      'flight-pad': FlightPad,
      'booking-engine': BookingEngine,
      filters: Filters,
      'no-results': NoResults,
      'no-results-suggestions': NoResultsSuggestions,
      'date-suggestions': DateSuggestions,
      'search-error': SearchError,
      'marketing-pad': MarketingPad,
      'trigger-booking-engine': TriggerBookingEngine,
    },
    props: {
      search_error: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        is_open: false,
        show_filters: false,
        show_search_error: this.search_error,
        searching_suggestions: false,
      };
    },
    computed: {
      ...mapState('liveflight', [
        'loading',
        'search_request',
        'can_search_suggestions',
        'unset_flight_offer_id',
      ]),
      ...mapGetters('liveflight', ['results', 'suggestions']),
      sorting() {
        return Object.entries(sorting_options).map(([key, value]) => ({
          by: key,
          ...value,
        }));
      },
      has_results() {
        if (Object.keys(this.results || {}).length <= 0) {
          return false;
        }
        const { filtered: filtered_results = [] } = this.filtered_flights || {};
        return filtered_results.length > 0;
      },
      has_suggestions() {
        const { by_radius = [], by_time_window = [] } = this.suggestions || {};
        return by_radius.length + by_time_window.length > 0;
      },
      best_picks() {
        const { best_picks = {} } = this.results || {};
        return best_picks;
      },
      current_sort() {
        return get_sorting();
      },
      loader_messages() {
        // undefined falls back to the loader's default messages
        return this.searching_suggestions
          ? loading_messages.search_suggestions.messages
          : undefined;
      },
      items() {
        return add_extra_components_to_results(
          this.filtered_flights.filtered,
          '_key'
        );
      },
      filtered_flights() {
        const filtered_flights = filter_flight_offers();
        const { best: best_pick = {} } = this.best_picks;
        const filtered = sort_flight_offers(
          filtered_flights.filtered,
          best_pick.rankings || {}
        );

        custom_event({
          event: 'view_item_list',
          ecommerce: {
            currency: 'EUR',
            items: filtered.map(parse_flight_offer_for_ecommerce_item),
            transaction_id: '',
            affiliation: '',
            value: 0,
            tax: 0,
            shipping: 0,
            coupon: '',
          },
        });

        return {
          filtered,
          filtered_all_carriers: filtered_flights.filtered_all_carriers,
        };
      },
    },
    mounted() {
      this.executeSearch();
    },
    methods: {
      set_sorting,
      executeSearch() {
        this.$store.commit('liveflight/set_loading', true);
        if (
          !this.search_request ||
          Object.keys(this.search_request).length === 0
        ) {
          this.$store.commit('liveflight/end_search');
          return;
        }
        search_all(this.search_request)
          .then((parsed_flight_offers) => {
            let flights = parsed_flight_offers;
            if (this.unset_flight_offer_id) {
              flights = parsed_flight_offers.filter(
                (o) => o && o.id !== this.unset_flight_offer_id
              );
            }
            this.$store.commit('liveflight/results', {
              flights,
              offers: flights,
              best_picks: {},
            });

            if (
              get_config(
                'guiSettings.enableNoResultsSearchSuggestions',
                true
              ) &&
              this.can_search_suggestions &&
              parsed_flight_offers.length === 0
            ) {
              // Chained so the loader stays up until suggestions settle
              return this.handleSearchSuggestions();
            }
            return undefined;
          })
          .finally(() => {
            // unset_flight_offer_id is one-shot — clear regardless of
            // success/failure so a rejected fan-out can't leak the stash
            // into the next search.
            if (this.unset_flight_offer_id) {
              this.$store.commit('liveflight/unset_flight_offer_id', '');
            }
            this.$store.commit('liveflight/end_search');
          });
      },
      onSearch() {
        this.is_open = false;
        this.show_search_error = false;
        // When searching from results page, just execute the search (already on results page)
        this.executeSearch();
      },
      toggle_filters() {
        this.show_filters = !this.show_filters;
        if (this.show_filters) {
          document.body.classList.add('overflow-hidden');
        } else {
          document.body.classList.remove('overflow-hidden');
        }
      },
      open_filters() {
        // From xl up the sidebar is always visible, so opening it (and locking
        // body scroll) only makes sense on smaller screens where it's off-canvas.
        if (window.matchMedia('(min-width: 1200px)').matches) return;
        this.show_filters = true;
        document.body.classList.add('overflow-hidden');
      },
      handleSearchSuggestions() {
        this.$store.commit('liveflight/can_search_suggestions', false);
        this.$store.commit('liveflight/suggestions', {
          by_radius: [],
          by_time_window: [],
        });
        this.$store.commit('liveflight/date_suggestions', []);

        if (Object.keys(this.search_request).length === 0) {
          return Promise.resolve();
        }

        this.searching_suggestions = true;

        return search_all_suggestions(this.search_request)
          .then(
            ({ by_radius, by_time_window }) => {
              this.$store.commit('liveflight/suggestions', {
                by_radius,
                by_time_window,
              });
              this.$store.commit('liveflight/date_suggestions', by_time_window);
            },
            () => {
              this.$store.commit('liveflight/suggestions', {
                by_radius: [],
                by_time_window: [],
              });
            }
          )
          .finally(() => {
            this.searching_suggestions = false;
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .result-view--initial-loading {
    padding: 3rem 0 4rem 0;
    @include media-breakpoint-up(lg) {
      padding: 6rem 0 8rem 0;
    }
  }

  .result-view--sticky {
    position: sticky;
    top: 1rem;
  }

  // Lets wide content (e.g. swipers) shrink instead of wrapping the column
  // below the filters.
  .result-view--main {
    min-width: 0;
  }

  .filters {
    &__sticky-wrapper {
      align-self: flex-start;
      top: 0;
      min-height: 100vh;
      overflow-y: scroll;
      box-shadow: 10px 0 20px 0 rgba(0, 0, 0, 0.3);
      width: 100%;
      z-index: 999;

      @include hide-scrollbar;

      @include media-breakpoint-up(xl) {
        box-shadow: none;
        z-index: 0;
      }

      @include media-breakpoint-down(lg) {
        max-width: none;
        min-height: auto;
        align-self: auto;
        transform: translateX(100%);
        transition: $transition-base;
        display: flex;
        flex-direction: column;
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        overflow: hidden;
        background-color: $white;

        &.visible {
          transform: translateX(0);
        }
      }
    }

    &__head {
      margin-bottom: $grid-gutter-width/2;

      @include media-breakpoint-down(lg) {
        display: flex;
        justify-content: space-between;
        background: $white;
        padding: $grid-gutter-width/2 0;
        z-index: 2;
        border-bottom: 1px solid $gray-200;
        min-height: 55px;
        margin-bottom: 0;
      }

      &-element {
        margin-bottom: 0;
        line-height: normal;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      &-title {
        font-size: $font-size-lg;
        color: var(--primary);
        font-weight: $vb-font-weight-semibold;
        display: inline-block;
      }
    }

    &__btn {
      &-toggle {
        @include media-breakpoint-up(xl) {
          display: none;
        }
      }

      &-container {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: $grid-gutter-width / 2;
        margin-bottom: $grid-gutter-width / 2;

        @include media-breakpoint-up(md) {
          justify-content: flex-end;
        }
      }
    }
  }

  .section {
    &--booking-engine {
      margin: 0;
      position: relative;

      &:after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 50%;
        left: 0;
        right: 0;
        background-color: var(--extra1); // same as header
        z-index: -2;
        display: none;

        @include media-breakpoint-up(lg) {
          display: block;
        }
      }
    }
    &--filters {
      position: relative;

      @at-root .overflow-hidden & {
        z-index: 999;
      }
    }
  }

  .flightoffer--itineraries {
    height: inherit;
  }
</style>
